import type { EndUser, Product, ProductQuota } from './types'

/** The User Limits page's pure rules (MS-822), split out of the page so each
 * is testable on its own and the page stays a composition. */

/** The sort select: the prototype's `% used` / `Email` / `Limit`, plus
 * `Username` from spec §16 (OQ12, a recorded deviation). */
export type SortKey = 'pct' | 'username' | 'email' | 'limit'
export type SortOrder = 'asc' | 'desc'

/** One product's default-limit draft: switched on or off, and the figure
 * (null only when none was ever set — the field shows "Set a limit"). */
export interface DefaultDraft {
  enabled: boolean
  value: number | null
}

/** One product's Account Defaults draft (the prototype's `snapshot()`
 * entry): the default limit plus the "New users" switch — register a new
 * user automatically on their first usage, or serve only the users listed
 * (MS-996). Both are saved by the page Save. */
export interface BaselineDraft extends DefaultDraft {
  autoRegister: boolean
}
export type DefaultsDraft = Record<Product, BaselineDraft>

function savedOne(quota: readonly ProductQuota[], product: Product): BaselineDraft {
  const q = quota.find((row) => row.product === product)
  // The `defaultUserLimit` fallbacks keep a page served during a rolling
  // deploy right against an API from before MS-822, which sends neither
  // field (ROLLBACK_RUNBOOK.md §5); likewise an API from before MS-996 sends
  // no `autoRegisterUsers`, and registration was always automatic then.
  return {
    enabled: q?.defaultUserLimitEnabled ?? q?.defaultUserLimit != null,
    value: q?.storedDefaultUserLimit ?? q?.defaultUserLimit ?? null,
    autoRegister: q?.autoRegisterUsers ?? true,
  }
}

/** What the server holds, as a draft: on/off and the kept figure (OQ2). */
export function savedDefaults(quota: readonly ProductQuota[]): DefaultsDraft {
  return { 'Mambu MCP': savedOne(quota, 'Mambu MCP'), 'Mambu Agents': savedOne(quota, 'Mambu Agents') }
}

/** Whether saving `draft` would change the default limit. Off is off
 * whatever figure it keeps, so toggling on and back off is not a change. */
export function defaultChanged(saved: DefaultDraft, draft: DefaultDraft): boolean {
  if (saved.enabled !== draft.enabled) return true
  return draft.enabled && (saved.value ?? 0) !== (draft.value ?? 0)
}

/** Whether saving `draft` would switch automatic registration (MS-996). */
export function registrationChanged(saved: BaselineDraft, draft: BaselineDraft): boolean {
  return saved.autoRegister !== draft.autoRegister
}

/** Whether saving `draft` would change anything on the Account Defaults
 * card — the page's dirty state and what the Save confirms. */
export function baselineChanged(saved: BaselineDraft, draft: BaselineDraft): boolean {
  return registrationChanged(saved, draft) || defaultChanged(saved, draft)
}

/** The PUT body's `defaultUserLimit`: the figure when on (0 when none was
 * ever set, OQ2), null when off. */
export function defaultBody(draft: DefaultDraft): number | null {
  return draft.enabled ? (draft.value ?? 0) : null
}

/** The default-limit half of the prototype's `baselineChanges`: which
 * sentence the "Confirm change" modal states for the default limit (the
 * registration half is `registrationChanged`, stated first). */
export type DefaultChange =
  | { kind: 'enabled' }
  | { kind: 'disabled' }
  | { kind: 'changed'; value: number }
  | { kind: 'noChange' }

export function describeChange(saved: DefaultDraft, draft: DefaultDraft): DefaultChange {
  if (saved.enabled !== draft.enabled) return { kind: draft.enabled ? 'enabled' : 'disabled' }
  if (draft.enabled && (saved.value ?? 0) !== (draft.value ?? 0)) {
    return { kind: 'changed', value: draft.value ?? 0 }
  }
  return { kind: 'noChange' }
}

function pctUsed(u: EndUser): number {
  // `renderRows`: a null or 0 limit sorts as -1, below every real share.
  return u.effectiveLimit ? (u.usageCount / u.effectiveLimit) * 100 : -1
}

function byCodeUnit(a: string, b: string): number {
  if (a < b) return -1
  return a > b ? 1 : 0
}

/** The prototype's `renderRows` sort, on committed data only — so editing
 * the default never reshuffles the list (T-LIM-06). `username` sorts by full
 * name with the nameless last in both orders, then by email (OQ12). */
export function sortUsers(users: readonly EndUser[], key: SortKey, order: SortOrder): EndUser[] {
  const dir = order === 'asc' ? 1 : -1
  return [...users].sort((a, b) => {
    switch (key) {
      case 'email':
        return byCodeUnit(a.email, b.email) * dir
      case 'limit':
        return ((a.effectiveLimit ?? -1) - (b.effectiveLimit ?? -1)) * dir
      case 'username': {
        if (a.fullName === null || b.fullName === null) {
          if (a.fullName !== b.fullName) return a.fullName === null ? 1 : -1
          return byCodeUnit(a.email, b.email)
        }
        return a.fullName.localeCompare(b.fullName) * dir || byCodeUnit(a.email, b.email)
      }
      case 'pct':
      default:
        return (pctUsed(a) - pctUsed(b)) * dir
    }
  })
}

/** The row input holds a figure, or the word "Default" (in English or the
 * reader's language), or nothing. */
export function isDefaultWord(raw: string, word: string): boolean {
  const v = raw.trim().toLowerCase()
  return v === 'default' || v === word.toLowerCase()
}

/** The prototype's `toInt`: the digits, or 0. */
export function toInt(raw: string): number {
  const digits = raw.replace(/\D/g, '')
  return digits === '' ? 0 : Number(digits)
}

/** What a row's Save sends: a figure, or null — "follow the default" — for
 * the word or an empty field (OQ9: no per-user "no limit" while a default is
 * on). */
export function rowTarget(raw: string, word: string): number | null {
  if (raw.trim() === '' || isDefaultWord(raw, word)) return null
  return toInt(raw)
}

/** The prototype's `.num-fmt` live formatting: the word "Default" is left
 * alone, anything else keeps its digits with thousands separators. */
export function formatRowInput(raw: string, word: string, locale: string): string {
  if (isDefaultWord(raw, word)) return raw
  const digits = raw.replace(/\D/g, '')
  return digits === '' ? '' : Number(digits).toLocaleString(locale)
}

/** Whether a row's typed value differs from what is saved — "differs from
 * what is committed", not "was typed in" (`setRowDirty`). */
export function isRowDirty(user: EndUser, raw: string | undefined, word: string): boolean {
  return raw !== undefined && rowTarget(raw, word) !== user.userLimit
}

/** `drafts` with `key` set to `raw`, or without it when `raw` is undefined. */
export function withRowDraft(
  drafts: Readonly<Record<string, string>>,
  key: string,
  raw: string | undefined
): Record<string, string> {
  const next = { ...drafts }
  if (raw === undefined) delete next[key]
  else next[key] = raw
  return next
}

/** A user drafted by Add user, not yet written (the prototype's
 * `add-limit-user-save`, which only adds them to the page's map until the
 * page Save): the email, stripped and lower-cased, and the limit they start
 * on — a figure, or null to start on the default (`addLimitChoice`'s
 * inherit: the default while one is enforced, no limit otherwise). */
export interface PendingAdd {
  email: string
  limit: number | null
  /** Set when a page Save sent this add and the server refused that address
   * (422 `end_user_invalid_email`): the add stays drafted, its row flagged,
   * and the rest of the Save goes on (MS-996 review, §53). */
  refused?: 'invalidEmail'
}

/** One product's drafted user changes: the users added and the
 * registrations deleted (by id), both applied by the page Save and dropped
 * by its Discard. */
export interface UsersDraft {
  adds: readonly PendingAdd[]
  deletes: readonly string[]
}
export type UsersDrafts = Record<Product, UsersDraft>

export const NO_USER_CHANGES: UsersDraft = { adds: [], deletes: [] }
export const noUserChanges = (): UsersDrafts => ({
  'Mambu MCP': NO_USER_CHANGES,
  'Mambu Agents': NO_USER_CHANGES,
})

/** Whether the page Save has a user to add or delete on this product. */
export function usersChanged(draft: UsersDraft): boolean {
  return draft.adds.length > 0 || draft.deletes.length > 0
}

const PENDING_PREFIX = 'pending:'

/** The row id a drafted user is listed under until the Save writes them. */
export const pendingId = (email: string) => `${PENDING_PREFIX}${email}`
export const isPendingId = (id: string) => id.startsWith(PENDING_PREFIX)

/** A drafted user as a list row: drawn like any other row (the prototype
 * renders them through the same `renderRows`), with no usage yet. `inForce`
 * is what a user on the default runs at, as drafted. */
export function pendingEndUser(add: PendingAdd, product: Product, inForce: number | null): EndUser {
  return {
    id: pendingId(add.email),
    product,
    subjectId: null,
    email: add.email,
    fullName: null,
    tenantId: null,
    tenantName: null,
    userLimit: add.limit,
    effectiveLimit: add.limit ?? inForce,
    usageCount: 0,
    active: true,
    isDefault: add.limit === null && inForce !== null,
  }
}

/** What a user on the default runs at for a default draft: its figure while
 * on (0 when none was ever set), no limit while off. */
export function inForceLimit(draft: DefaultDraft | undefined): number | null {
  return draft?.enabled ? (draft.value ?? 0) : null
}

/** The rows in `sortUsers` order, a drafted user sorted as if on `sortBasis`
 * — the *saved* default — rather than the drafted one their row shows, so
 * typing in the default never reorders the list, pending rows included
 * (T-LIM-06). Saved rows already sort on what the server committed. */
export function sortListed(
  rows: readonly EndUser[],
  adds: readonly PendingAdd[],
  sortBasis: number | null,
  key: SortKey,
  order: SortOrder
): EndUser[] {
  const byId = new Map(rows.map((u) => [u.id, u]))
  const basis = rows.map((u) => {
    const add = isPendingId(u.id) ? adds.find((a) => pendingId(a.email) === u.id) : undefined
    return add ? pendingEndUser(add, u.product, sortBasis) : u
  })
  return sortUsers(basis, key, order).map((u) => byId.get(u.id) ?? u)
}

/** The Add-user POST's `initialLimit` for a drafted user. */
export function initialLimitBody(add: PendingAdd): { mode: 'default' } | { mode: 'custom'; value: number } {
  return add.limit === null ? { mode: 'default' } : { mode: 'custom', value: add.limit }
}

/** A list of addresses for a toast, as the prototype's `join(', ')`, with
 * Chinese's own enumeration comma ("a@x.com、b@y.com"). Not
 * `Intl.ListFormat`: its conjunction glues Thai/Burmese "and" onto the
 * address ("a@x.comและb@y.com"), which leaves the address unreadable and
 * uncopyable. The toast's verb agrees with the count (`_one`/`_other`). */
export function formatList(items: readonly string[], language: string): string {
  return items.join(language === 'zh' ? '、' : ', ')
}
